import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ShoppingCart, Clock, TrendingUp, TrendingDown, Minus, Package, RefreshCw, Plus, Trash2, Printer, Send, Check, ListChecks, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api` });

const ChefCommande = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cart, setCart] = useState({});
  const [showCart, setShowCart] = useState(false);
  const [manualItems, setManualItems] = useState([]);
  const [showAddManual, setShowAddManual] = useState(false);
  const [newItem, setNewItem] = useState({ nom: '', quantite: 1, unite: '', fournisseur: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/commande/conseil');
      setData(res.data);
    } catch {
      toast.error('Erreur chargement conseils');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.suggestions;
    if (filter === 'a_commander') return data.suggestions.filter(s => s.a_commander);
    return data.suggestions.filter(s => s.urgence === filter);
  }, [data, filter]);

  const fournisseurs = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      const f = s.fournisseur || 'Autre';
      if (!map[f]) map[f] = [];
      map[f].push(s);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  // Cart functions
  const toggleCart = (product) => {
    const key = product.nom;
    setCart(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          nom: product.nom,
          quantite: product.quantite_moyenne || 1,
          unite: product.unite || '',
          fournisseur: product.fournisseur || '',
          prix: product.prix_unitaire || 0,
        };
      }
      return next;
    });
  };

  const updateCartQty = (key, qty) => {
    setCart(prev => ({ ...prev, [key]: { ...prev[key], quantite: Math.max(0.1, qty) } }));
  };

  const removeFromCart = (key) => {
    setCart(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const addManualItem = () => {
    if (!newItem.nom) { toast.error('Nom requis'); return; }
    const key = `manual_${newItem.nom}_${Date.now()}`;
    setCart(prev => ({
      ...prev,
      [key]: { ...newItem, prix: 0 }
    }));
    setManualItems(prev => [...prev, key]);
    setNewItem({ nom: '', quantite: 1, unite: '', fournisseur: '' });
    setShowAddManual(false);
    toast.success(`${newItem.nom} ajouté à la liste`);
  };

  const selectAllUrgent = () => {
    if (!data) return;
    const urgents = data.suggestions.filter(s => s.a_commander);
    const newCart = { ...cart };
    urgents.forEach(p => {
      if (!newCart[p.nom]) {
        newCart[p.nom] = {
          nom: p.nom, quantite: p.quantite_moyenne || 1,
          unite: p.unite || '', fournisseur: p.fournisseur || '', prix: p.prix_unitaire || 0,
        };
      }
    });
    setCart(newCart);
    toast.success(`${urgents.length} produits ajoutés`);
  };

  // Cart grouped by supplier
  const cartBySupplier = useMemo(() => {
    const map = {};
    Object.entries(cart).forEach(([key, item]) => {
      const f = item.fournisseur || 'Autre';
      if (!map[f]) map[f] = [];
      map[f].push({ ...item, key });
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [cart]);

  const cartTotal = Object.values(cart).reduce((s, i) => s + (i.prix || 0) * (i.quantite || 0), 0);
  const cartCount = Object.keys(cart).length;

  const printList = () => {
    const lines = [];
    lines.push('═══════════════════════════════════');
    lines.push('   LISTE DE COURSES - LE MAS');
    lines.push('   ' + new Date().toLocaleDateString('fr-FR'));
    lines.push('═══════════════════════════════════\n');
    cartBySupplier.forEach(([fournisseur, items]) => {
      lines.push(`▸ ${fournisseur.toUpperCase()}`);
      lines.push('─────────────────────────────────');
      items.forEach(item => {
        const qty = item.quantite % 1 === 0 ? item.quantite : item.quantite.toFixed(1);
        lines.push(`  □ ${item.nom} — ${qty} ${item.unite}`);
      });
      lines.push('');
    });
    lines.push(`Total estimé: ${cartTotal.toFixed(2)}€`);
    lines.push('═══════════════════════════════════');

    const w = window.open('', '_blank');
    w.document.write(`<pre style="font-family:monospace;font-size:14px;padding:20px;white-space:pre-wrap;">${lines.join('\n')}</pre>`);
    w.document.title = 'Liste de courses - Le Mas';
    w.print();
  };

  const sendToTelegram = async () => {
    const lines = ['<b>🛒 LISTE DE COURSES</b>\n'];
    cartBySupplier.forEach(([fournisseur, items]) => {
      lines.push(`<b>▸ ${fournisseur}</b>`);
      items.forEach(item => {
        const qty = item.quantite % 1 === 0 ? item.quantite : item.quantite.toFixed(1);
        lines.push(`  • ${item.nom} — ${qty} ${item.unite}`);
      });
      lines.push('');
    });
    lines.push(`<b>Estimation: ${cartTotal.toFixed(2)}€</b>`);

    try {
      await api.post('/telegram/send-message', {
        message: lines.join('\n'),
        role: 'patron',
        bot_type: 'chef'
      });
      toast.success('Liste envoyée sur Telegram');
    } catch {
      toast.error('Erreur envoi Telegram');
    }
  };

  const urgencyBadge = (urgence) => {
    switch (urgence) {
      case 'urgent': return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-400/10 text-red-400 font-medium">Urgent</span>;
      case 'a_prevoir': return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-400/10 text-orange-400 font-medium">A prévoir</span>;
      default: return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))]">OK</span>;
    }
  };

  const trendIcon = (trend) => {
    switch (trend) {
      case 'hausse': return <TrendingUp size={12} className="text-red-400" />;
      case 'baisse': return <TrendingDown size={12} className="text-green-400" />;
      default: return <Minus size={12} className="text-[rgb(var(--lm-text-muted))]" />;
    }
  };

  const fmt = (n) => n ? `${n.toFixed(2)}€` : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="commande-loading">
        <RefreshCw size={24} className="animate-spin text-lemas-green" />
      </div>
    );
  }

  const resume = data?.resume || {};

  return (
    <div className="space-y-4" data-testid="chef-commande">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="font-title text-xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <ShoppingCart size={20} className="text-lemas-green" /> Conseil de commande
          </h2>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Cochez les produits pour créer votre liste de courses</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={selectAllUrgent} variant="outline" className="border-orange-400/30 text-orange-400 hover:bg-orange-400/10" data-testid="select-all-urgent">
            <ListChecks size={14} className="mr-1" /> Tout urgent
          </Button>
          <Button size="sm" onClick={() => setShowCart(true)} className="bg-lemas-green text-lemas-bg hover:bg-lemas-green/90 relative" data-testid="open-cart-btn">
            <ShoppingCart size={14} className="mr-1" /> Liste
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-[rgb(var(--lm-text))] text-[10px] flex items-center justify-center">{cartCount}</span>
            )}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-[rgb(var(--lm-surface))]" data-testid="kpi-total">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] uppercase">Produits suivis</p>
            <p className="font-mono text-2xl text-[rgb(var(--lm-text))] mt-1">{resume.total_produits || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[rgb(var(--lm-surface))] border-orange-400/20" data-testid="kpi-commander">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-orange-400 uppercase">A commander</p>
            <p className="font-mono text-2xl text-orange-400 mt-1">{resume.a_commander || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[rgb(var(--lm-surface))] border-red-400/20" data-testid="kpi-urgent">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-red-400 uppercase">Urgents</p>
            <p className="font-mono text-2xl text-red-400 mt-1">{resume.urgents || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[rgb(var(--lm-surface))] border-lemas-green/20" data-testid="kpi-cout">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-lemas-green uppercase">Dans la liste</p>
            <p className="font-mono text-2xl text-lemas-green mt-1">{cartCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'Tous', count: data?.suggestions?.length },
          { id: 'a_commander', label: 'A commander', count: resume.a_commander },
          { id: 'urgent', label: 'Urgents', count: resume.urgents },
          { id: 'a_prevoir', label: 'A prévoir', count: data?.suggestions?.filter(s => s.urgence === 'a_prevoir').length },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.id ? 'bg-lemas-green/20 text-lemas-green' : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))]'}`}
            data-testid={`filter-${f.id}`}>
            {f.label} ({f.count || 0})
          </button>
        ))}
      </div>

      {/* Products by supplier */}
      {filtered.length === 0 ? (
        <Card className="bg-[rgb(var(--lm-surface))]">
          <CardContent className="py-12 text-center">
            <Package size={40} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
            <p className="text-[rgb(var(--lm-text-secondary))]">Aucun produit dans l'historique</p>
            <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Scannez des factures pour alimenter les conseils</p>
          </CardContent>
        </Card>
      ) : (
        fournisseurs.map(([fournisseur, products]) => (
          <Card key={fournisseur} className="bg-[rgb(var(--lm-surface))]" data-testid={`supplier-${fournisseur}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[rgb(var(--lm-text))] flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package size={14} className="text-lemas-green" />
                  {fournisseur}
                  <span className="text-xs text-[rgb(var(--lm-text-muted))] font-normal">({products.length})</span>
                </span>
                <span className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">
                  ~{fmt(products.reduce((s, p) => s + p.prix_unitaire * p.quantite_moyenne, 0))}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {products.map((p, i) => {
                  const inCart = !!cart[p.nom];
                  return (
                    <div key={i}
                      className={`flex items-center gap-3 py-2 px-3 rounded-lg cursor-pointer transition-all ${inCart ? 'bg-lemas-green/10 border border-lemas-green/30' : p.urgence === 'urgent' ? 'bg-red-400/5 hover:bg-red-400/10' : p.urgence === 'a_prevoir' ? 'bg-orange-400/5 hover:bg-orange-400/10' : 'bg-lemas-bg/30 hover:bg-[rgb(var(--lm-surface-elevated))]'}`}
                      data-testid={`product-row-${i}`}>
                      {/* Checkbox */}
                      <button onClick={() => toggleCart(p)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${inCart ? 'bg-lemas-green border-lemas-green' : 'border-white/20 hover:border-white/40'}`}
                        data-testid={`check-${p.nom}`}>
                        {inCart && <Check size={12} className="text-lemas-bg" />}
                      </button>

                      {/* Product info */}
                      <div className="flex-1 min-w-0" onClick={() => toggleCart(p)}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-sm font-medium truncate ${inCart ? 'text-lemas-green' : 'text-[rgb(var(--lm-text))]'}`}>{p.nom}</p>
                          {urgencyBadge(p.urgence)}
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">
                          <span>Dernier: {p.dernier_achat}</span>
                          <span className="flex items-center gap-0.5"><Clock size={9} /> {p.jours_depuis}j / ~{p.frequence_jours}j</span>
                        </div>
                      </div>

                      {/* Price + qty */}
                      <div className="flex items-center gap-3 shrink-0">
                        {inCart && (
                          <input type="number" step="0.5" min="0.1"
                            value={cart[p.nom]?.quantite || 0}
                            onChange={e => updateCartQty(p.nom, parseFloat(e.target.value) || 1)}
                            className="w-16 bg-lemas-bg border border-lemas-green/30 rounded px-2 py-1 text-xs font-mono text-center text-lemas-green"
                            onClick={e => e.stopPropagation()}
                            data-testid={`qty-${p.nom}`} />
                        )}
                        <div className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{fmt(p.prix_unitaire)}</span>
                            {p.unite && <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">/{p.unite}</span>}
                            {trendIcon(p.tendance_prix)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Cart panel (slide-in) */}
      {showCart && (
        <div className="fixed inset-0 z-50" data-testid="cart-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCart(false)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-md bg-[rgb(var(--lm-surface))] flex flex-col animate-in slide-in-from-right duration-200">
            {/* Cart header */}
            <div className="px-5 py-4 border-b border-[rgb(var(--lm-border))] flex items-center justify-between">
              <h3 className="font-title text-lg text-[rgb(var(--lm-text))] flex items-center gap-2">
                <ShoppingCart size={18} className="text-lemas-green" /> Liste de courses
                <span className="text-sm text-[rgb(var(--lm-text-secondary))] font-normal">({cartCount})</span>
              </h3>
              <button onClick={() => setShowCart(false)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={20} /></button>
            </div>

            {/* Cart items */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {cartCount === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart size={36} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
                  <p className="text-[rgb(var(--lm-text-secondary))]">Liste vide</p>
                  <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Cochez des produits pour les ajouter</p>
                </div>
              ) : (
                cartBySupplier.map(([fournisseur, items]) => (
                  <div key={fournisseur}>
                    <p className="text-xs text-lemas-green uppercase tracking-wider font-medium mb-2 flex items-center gap-1">
                      <Package size={12} /> {fournisseur}
                    </p>
                    <div className="space-y-1">
                      {items.map(item => (
                        <div key={item.key} className="flex items-center gap-3 bg-lemas-bg/50 rounded-lg px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[rgb(var(--lm-text))] truncate">{item.nom}</p>
                          </div>
                          <input type="number" step="0.5" min="0.1"
                            value={item.quantite}
                            onChange={e => updateCartQty(item.key, parseFloat(e.target.value) || 1)}
                            className="w-16 bg-lemas-bg border border-[rgb(var(--lm-border))] rounded px-2 py-1 text-xs font-mono text-center text-[rgb(var(--lm-text))]" />
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))] w-6">{item.unite}</span>
                          <button onClick={() => removeFromCart(item.key)} className="text-red-400/50 hover:text-red-400">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}

              {/* Add manual item */}
              {showAddManual ? (
                <div className="bg-lemas-bg/50 rounded-lg p-4 space-y-3" data-testid="add-manual-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <Input value={newItem.nom} onChange={e => setNewItem({ ...newItem, nom: e.target.value })}
                        placeholder="Nom du produit" className="bg-lemas-bg border-[rgb(var(--lm-border))] text-sm" data-testid="manual-nom" />
                    </div>
                    <Input type="number" value={newItem.quantite || ''} onChange={e => setNewItem({ ...newItem, quantite: parseFloat(e.target.value) || 1 })}
                      placeholder="Qté" className="bg-lemas-bg border-[rgb(var(--lm-border))] text-sm" data-testid="manual-qty" />
                    <Input value={newItem.unite} onChange={e => setNewItem({ ...newItem, unite: e.target.value })}
                      placeholder="Unité (kg, L...)" className="bg-lemas-bg border-[rgb(var(--lm-border))] text-sm" data-testid="manual-unite" />
                    <div className="col-span-2">
                      <Input value={newItem.fournisseur} onChange={e => setNewItem({ ...newItem, fournisseur: e.target.value })}
                        placeholder="Fournisseur" className="bg-lemas-bg border-[rgb(var(--lm-border))] text-sm" data-testid="manual-fournisseur" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addManualItem} className="bg-lemas-green text-lemas-bg flex-1" data-testid="confirm-manual">Ajouter</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddManual(false)} className="text-[rgb(var(--lm-text-secondary))]">Annuler</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddManual(true)}
                  className="w-full py-3 border-2 border-dashed border-[rgb(var(--lm-border))] rounded-lg text-sm text-[rgb(var(--lm-text-secondary))] hover:border-lemas-green/30 hover:text-lemas-green transition-colors flex items-center justify-center gap-2"
                  data-testid="add-manual-btn">
                  <Plus size={16} /> Ajouter un produit manuellement
                </button>
              )}
            </div>

            {/* Cart footer */}
            {cartCount > 0 && (
              <div className="px-5 py-4 border-t border-[rgb(var(--lm-border))] space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[rgb(var(--lm-text-secondary))]">Estimation totale</span>
                  <span className="font-mono text-lemas-green font-medium">{fmt(cartTotal)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={printList} className="bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))] hover:bg-white/20" data-testid="print-list">
                    <Printer size={14} className="mr-1" /> Imprimer
                  </Button>
                  <Button onClick={sendToTelegram} className="bg-lemas-green text-lemas-bg hover:bg-lemas-green/90" data-testid="send-telegram-list">
                    <Send size={14} className="mr-1" /> Telegram
                  </Button>
                </div>
                <Button variant="ghost" onClick={() => { setCart({}); toast.success('Liste vidée'); }}
                  className="w-full text-red-400/60 hover:text-red-400 hover:bg-red-400/10 text-xs" data-testid="clear-cart">
                  Vider la liste
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChefCommande;
